// B-165. The assistant's reply, tidied before it is drawn as markdown: Windows line endings and
// trailing spaces out, and three or more line breaks in a row (the model sometimes sends them)
// down to one blank line — never inside a ``` block, where every line is the content.
const CERCA = /^\s*(```|~~~)/;

export function prepararRespuesta(texto) {
  if (typeof texto !== "string") return "";
  const lineas = texto.replace(/\r\n?/g, "\n").split("\n");
  const salida = [];
  let enCodigo = false;
  let vacias = 0;
  for (const linea of lineas) {
    if (CERCA.test(linea)) enCodigo = !enCodigo;
    if (enCodigo) {
      salida.push(linea);
      vacias = 0;
      continue;
    }
    const limpia = linea.replace(/[ \t]+$/, "");
    if (limpia === "") {
      vacias += 1;
      if (vacias > 1) continue;
    } else {
      vacias = 0;
    }
    salida.push(limpia);
  }
  return salida.join("\n").trim();
}

// A figure as the assistant writes one: 140, $ 1.234.567, -$330.000, 12,5 %, (2). A date
// ("31 oct", "2026-10-31") or a word is not.
const CIFRA = /^[-+−]?\(?[-+−]?\$?\s?[\d.,]*\d[\d.,]*\s?%?\)?$/;

export function esCifra(texto) {
  const t = String(texto ?? "").trim();
  return t !== "" && CIFRA.test(t);
}

const textoDe = (nodo) =>
  nodo?.type === "text" ? nodo.value : (nodo?.children || []).map(textoDe).join("");

const filasDe = (seccion) =>
  (seccion?.children || []).filter((n) => n.type === "element" && n.tagName === "tr");

const celdasDe = (fila) =>
  (fila.children || []).filter((n) => n.type === "element" && (n.tagName === "td" || n.tagName === "th"));

function alinearTabla(tabla) {
  const secciones = (tabla.children || []).filter((n) => n.type === "element");
  const cuerpo = secciones.filter((n) => n.tagName === "tbody").flatMap(filasDe);
  if (!cuerpo.length) return;
  const columnas = Math.max(...cuerpo.map((f) => celdasDe(f).length));
  const numericas = [];
  for (let c = 0; c < columnas; c += 1) {
    const valores = cuerpo.map((f) => textoDe(celdasDe(f)[c]).trim()).filter(Boolean);
    if (valores.length && valores.every(esCifra)) numericas.push(c);
  }
  if (!numericas.length) return;
  const filas = secciones.flatMap((s) => (s.tagName === "tr" ? [s] : filasDe(s)));
  for (const fila of filas) {
    celdasDe(fila).forEach((celda, c) => {
      if (!numericas.includes(c)) return;
      celda.properties = celda.properties || {};
      // An alignment the model wrote (`--:`, `:--`) is its choice and stays.
      if (!celda.properties.align) celda.properties.align = "right";
    });
  }
}

function recorrer(nodo) {
  if (nodo?.type === "element" && nodo.tagName === "table") alinearTabla(nodo);
  (nodo?.children || []).forEach(recorrer);
}

// A rehype plugin: every column of a table whose body cells are all figures is aligned right,
// so amounts line up and can be compared at a glance.
export function alinearCifras() {
  return (arbol) => recorrer(arbol);
}

const ES_FILA = /^\s*\|/;
const ES_SEPARADOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

// While a reply is being written out, a table must not show as loose `|` lines: the row still being
// typed is held back, and so is a table whose `|---|` line has not arrived yet. What follows a table
// starts a paragraph of its own, so the typing cursor never lands inside a row.
export function textoParaEscritura(parcial) {
  if (typeof parcial !== "string" || parcial === "") return "";
  let lineas = parcial.split("\n");
  if (!parcial.endsWith("\n") && ES_FILA.test(lineas[lineas.length - 1])) lineas = lineas.slice(0, -1);
  while (lineas.length && lineas[lineas.length - 1] === "") lineas = lineas.slice(0, -1);
  let inicio = lineas.length;
  while (inicio > 0 && ES_FILA.test(lineas[inicio - 1])) inicio -= 1;
  const bloque = lineas.slice(inicio);
  if (bloque.length && (bloque.length < 2 || !ES_SEPARADOR.test(bloque[1]))) {
    lineas = lineas.slice(0, inicio);
    while (lineas.length && lineas[lineas.length - 1] === "") lineas = lineas.slice(0, -1);
  } else if (bloque.length) {
    return `${lineas.join("\n")}\n\n`;
  }
  return lineas.join("\n");
}
